<template>
  <!-- Segmentation Editor -->
  <ListSegmentationEditor
    v-if="showSegmentationEditor"
    :id-shop-list="selectedListId"
    @close="showSegmentationEditor = false"
    @saved="onSegmentationSaved"
  />

  <!-- Import Modal -->
  <ImportModal
    v-if="showImportModal"
    :title="t('myLists.manage.customers.import.title')"
    import-type="contact"
    :params="{ listId: selectedListId }"
    @close="showImportModal = false"
  />

  <!-- Action Components -->
  <RenameListAction
    ref="renameListRef"
    @confirm="onRenameConfirm"
  />
  <DuplicateListAction
    ref="duplicateListRef"
    @confirm="onDuplicateConfirm"
  />
  <DeleteListAction
    ref="deleteListRef"
    @confirm="onDeleteConfirm"
  />

  <div class="lists-manage-page">
    <PageHeader
      :title="t('myLists.manage.title')"
      :summary="t('myLists.manage.summary')"
    >
      <template #actions>
        <ListCreatePanelV2 v-if="findPermission('lists-segments.create')" />
      </template>
    </PageHeader>

    <!-- Content Section with DataTable -->
    <div class="page-content">
      <div class="ListsManage">
        <SpmTableAPI
          v-if="apiCall && columns.length > 0"
          ref="childComponentRef"
          :api-call="apiCall"
          :show-reload-button="true"
          :columns="columns"
          data-key="id_shop_list"
          :row-actions-options="rowActions"
          :persistent-filters="persistentFilters"
          :initial-sort="initialSort"
        >
          <!-- Slot pour la colonne Name -->
          <template #cell-name="{ data }">
            <div class="list-info">
              <div class="list-name-line">
                <IntegrationOwnerBadge
                  v-if="data.owner_integration"
                  :owner-integration="data.owner_integration"
                />
                <span class="list-name font-bold">
                  {{ data.name || data.Name || data.list_name }}
                </span>
              </div>
              <SpmDateDisplay
                :value="data.date_add"
                format="date"
                icon="fa-light fa-calendar-circle-plus"
                size="sm"
              />
            </div>
          </template>

          <!-- Slot pour la colonne Type -->
          <template #cell-type="{ data }">
            <SpmBadge
              :label="t(`myLists.manage.listTypes.${data.type || 'list'}`)"
              :icon="getTypeBadgeIcons(data.type)"
              :colors="getTypeBadgeColors(data.type)"
              uppercase
            />
          </template>

          <!-- Slot pour la colonne Total Contacts -->
          <template #cell-total_contacts="{ data }">
            <span
              v-if="data.total_contacts > 0"
              class="contacts-count"
            >
              <i class="fa fa-users" />
              {{ formatContactsCount(data.total_contacts) }}
            </span>
            <span
              v-else
              class="contacts-count contacts-count--empty"
            >-</span>
          </template>
        </SpmTableAPI>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import PageHeader from '@/components/layout/PageHeader.vue';
import { useRouter } from 'vue-router';
import { findPermission } from '@/composables/User';
import { NestApiType, nestCall } from '@/composables/nestApi';
import { showToastError, showToastSuccess } from '@/helpers';
import ListCreatePanelV2 from '@/components/mes-lists/ListCreatePanelV2.vue';
import ListSegmentationEditor from '@/components/mes-lists/ListSegmentationEditor.vue';
import ImportModal from '@/components/import/ImportModal.vue';
import SpmTableAPI from '@/components/table-rest-api/SpmTableAPI.vue';
import SpmBadge from '@/components/table-rest-api/SpmBadge.vue';
import SpmDateDisplay from '@/components/table-rest-api/SpmDateDisplay.vue';
import { formatInteger } from '@/components/table-rest-api/formatters';
import type {
  SpmColumnConfig,
  ApiCallConfig,
  SpmApiFilter,
  SpmActiveSort,
} from '@/components/table-rest-api/types';
import { SpmFilterOperator } from '@/components/table-rest-api/filters';
import IntegrationOwnerBadge from '@/components/integrations/IntegrationOwnerBadge.vue';
import RenameListAction from './ListsManageActions/RenameListAction.vue';
import DuplicateListAction from './ListsManageActions/DuplicateListAction.vue';
import DeleteListAction from './ListsManageActions/DeleteListAction.vue';
import useListsActions from './ListsManageActions/useListsActions';
import type {
  RenameListResult,
  DuplicateListResult,
  DeleteListResult,
} from './ListsManageActions/types';

export default defineComponent({
  name: 'ListsManageV2',
  components: {
    PageHeader,
    SpmTableAPI,
    SpmBadge,
    SpmDateDisplay,
    IntegrationOwnerBadge,
    ListCreatePanelV2,
    RenameListAction,
    DuplicateListAction,
    DeleteListAction,
    ListSegmentationEditor,
    ImportModal,
  },

  setup() {
    const { t } = useI18n();
    const router = useRouter();
    const childComponentRef = ref();

    // Action component refs
    const renameListRef = ref(null);
    const duplicateListRef = ref(null);
    const deleteListRef = ref(null);

    // Modal states
    const showSegmentationEditor = ref(false);
    const showImportModal = ref(false);
    const selectedListId = ref<number>(0);

    /**
     * Reload table data after an action
     */
    const reloadTableData = () => {
      if (childComponentRef.value) {
        childComponentRef.value.reloadData();
      }
    };

    const setLoading = async (loading: boolean) => {
      if (childComponentRef.value) {
        await childComponentRef.value.setLoading(loading);
      }
    };

    // Handlers for opening modals
    const handleOpenImportModal = (listId: number) => {
      selectedListId.value = listId;
      showImportModal.value = true;
    };

    const handleOpenSegmentationEditor = (listId: number) => {
      selectedListId.value = listId;
      showSegmentationEditor.value = true;
    };

    const onSegmentationSaved = () => {
      showSegmentationEditor.value = false;
      router.push(`/lists/manage-contacts/${selectedListId.value}`);
    };

    // Use the composable for row actions
    const { rowActions } = useListsActions({
      renameListRef,
      duplicateListRef,
      deleteListRef,
      onOpenImportModal: handleOpenImportModal,
      onOpenSegmentationEditor: handleOpenSegmentationEditor,
    });

    // Action confirm handlers
    const onRenameConfirm = async (result: RenameListResult) => {
      try {
        setLoading(true);
        const response = await nestCall(
          'patch',
          NestApiType.CLIENT_API,
          `/lists/${result.listId}`,
          {},
          '',
          { name: result.newName },
          true,
        );
        if (response && response.id_shop_list) {
          showToastSuccess(t('myLists.manage.actions.listRename.success'));
          reloadTableData();
        } else {
          showToastError(t('myLists.manage.actions.listRename.error'));
        }
      } catch (error) {
        console.error('Rename list API error:', error);
        showToastError(t('myLists.manage.actions.listRename.error'));
      } finally {
        setLoading(false);
      }
    };

    const onDuplicateConfirm = async (result: DuplicateListResult) => {
      try {
        setLoading(true);
        const payload = {
          action_type: 'duplicate',
          selection_type: 'all',
          selected_ids: [],
          filters: [],
          source_list_id: result.listId,
          new_list_name: result.newName,
        };

        const response = await nestCall(
          'post',
          NestApiType.CLIENT_API,
          '/list-actions',
          {},
          '',
          payload,
          true,
        );

        if (response && response.success) {
          if (response.message === 'request_pending') {
            showToastSuccess(t('myLists.manage.actions.listDuplicate.queued'));
          } else {
            showToastSuccess(t('myLists.manage.actions.listDuplicate.success'));
          }
          reloadTableData();
        } else {
          showToastError(t('myLists.manage.actions.listDuplicate.error'));
        }
      } catch (error) {
        console.error('Duplicate list API error:', error);
        showToastError(t('myLists.manage.actions.listDuplicate.error'));
      } finally {
        setLoading(false);
      }
    };

    const onDeleteConfirm = async (result: DeleteListResult) => {
      try {
        setLoading(true);
        const response = await nestCall(
          'delete',
          NestApiType.CLIENT_API,
          `/lists/${result.listId}`,
          {},
          '',
          {},
          true,
        );

        if (response && response.statusCode === 200) {
          showToastSuccess(t('myLists.manage.actions.listDelete.success'));
          reloadTableData();
        } else {
          showToastError(t('myLists.manage.actions.listDelete.error'));
        }
      } catch (error) {
        console.error('Delete list API error:', error);
        showToastError(t('myLists.manage.actions.listDelete.error'));
      } finally {
        setLoading(false);
      }
    };

    // API call configuration
    const apiCall = ref<ApiCallConfig | null>({
      type_api: NestApiType.CLIENT_API,
      path: '/lists',
      secure: true,
    });

    // Persistent filters - only fetch valid and active lists
    const persistentFilters: SpmApiFilter[] = [
      {
        field: 'valid',
        operator: SpmFilterOperator.EQUALS,
        values: 1,
      },
      {
        field: 'active',
        operator: SpmFilterOperator.EQUALS,
        values: 1,
      },
    ];

    // Initial sort - most recently created lists first
    const initialSort: SpmActiveSort = {
      field: 'date_add',
      order: 'desc',
    };

    // Columns configuration - only name, type, total_contacts (filterable and sortable)
    const columns: SpmColumnConfig[] = [
      {
        id: 'name',
        header: t('myLists.manage.headers.name'),
        maxWidth: '40%',
        fields: [
          {
            name: 'name',
            label: t('myLists.manage.headers.name'),
            sortable: true,
            filterable: true,
            filterConfig: { type: 'textInput' },
          },
          'id_shop_list',
          {
            name: 'date_add',
            label: t('myLists.manage.headers.dateAdd'),
            sortable: true,
            filterable: true,
            filterConfig: { type: 'dateRange' },
          },
        ],
      },
      {
        id: 'type',
        header: t('myLists.manage.headers.type'),
        fields: [
          {
            name: 'type',
            label: t('myLists.manage.headers.type'),
            sortable: true,
            filterable: true,
            filterConfig: {
              type: 'multiSelect',
              options: [
                { label: t('myLists.manage.listTypes.list'), value: 'list' },
                { label: t('myLists.manage.listTypes.segment'), value: 'segment' },
              ],
            },
          },
        ],
      },
      {
        id: 'total_contacts',
        header: t('myLists.manage.headers.totalContacts'),
        fields: [
          {
            name: 'total_contacts',
            label: t('myLists.manage.headers.totalContacts'),
            sortable: true,
            filterable: true,
            filterConfig: { type: 'numberInput' },
          },
        ],
      },
    ];

    const getTypeBadgeIcons = (type: string | null | undefined) => {
      if (type === 'segment') {
        return 'fa fa-filter';
      }
      return 'fa fa-list';
    };

    // Helper function for type badge colors
    const getTypeBadgeColors = (type: string | null | undefined) => {
      if (type === 'segment') {
        return { bg: 'var(--purple-100)', color: 'var(--purple-700)' };
      }
      return { bg: 'var(--blue-100)', color: 'var(--blue-700)' };
    };

    // Format contacts count using shared formatter
    const formatContactsCount = (value: number | null | undefined): string => formatInteger(value);

    return {
      t,
      columns,
      childComponentRef,
      findPermission,
      apiCall,
      rowActions,
      persistentFilters,
      initialSort,
      getTypeBadgeColors,
      getTypeBadgeIcons,
      formatContactsCount,
      // Action refs
      renameListRef,
      duplicateListRef,
      deleteListRef,
      // Action handlers
      onRenameConfirm,
      onDuplicateConfirm,
      onDeleteConfirm,
      // Modal states
      showSegmentationEditor,
      showImportModal,
      selectedListId,
      onSegmentationSaved,
    };
  },
});
</script>

<style lang="scss" scoped>
/* Page layout */
.lists-manage-page {
  display: flex;
  flex-direction: column;
  min-height: 100%;
}

/* List info styling */
.list-info {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.list-name {
  font-weight: 500;
  color: var(--text-color);
}

.list-date {
  font-size: 0.8rem;
  color: var(--text-color-secondary);

  i {
    margin-right: 0.35rem;
    font-size: 0.7rem;
  }
}

/* Type badge styling */
.type-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.625rem;
  font-size: 0.75rem;
  font-weight: 500;
  border-radius: 4px;
  text-transform: uppercase;
  letter-spacing: 0.025em;
}

.type-badge--list {
  background-color: var(--blue-100);
  color: var(--blue-700);
}

.type-badge--segment {
  background-color: var(--purple-100);
  color: var(--purple-700);
}

/* Contacts count styling */
.contacts-count {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--text-color);

  i {
    color: var(--text-color-secondary);
  }
}

.contacts-count--empty {
  color: var(--text-color-secondary);
}
</style>
